import {AutoTableInterface} from "@/features/types/AutoTableInterface";
import {HeaderContext} from "@tanstack/react-table";
import React, {useRef} from "react";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/shared/components/ui/tooltip";
import {ArrowDown01, ArrowDownAZ, ArrowUp01, ArrowUpAZ} from "lucide-react";
import {FilterKey} from "@/features/types/AutoTableFilterInterface";
import {Button} from "@/shared/components/ui/button";

interface AutoTableHeaderFilterProps<T> {
    info: HeaderContext<AutoTableInterface, T>;
    name: string;
    isFilterable: boolean;
    onHeaderClick?: (columnId: FilterKey, e: React.MouseEvent<HTMLButtonElement>) => void;
}

export function AutoTableHeaderFilter<TValue>({
                                                  info,
                                                  name,
                                                  isFilterable,
                                                  onHeaderClick,
                                              }: AutoTableHeaderFilterProps<TValue>) {
    const {column} = info;
    const columnId = column.id as FilterKey;

    const sorted = column.getIsSorted();
    const isNumeric = column.columnDef.meta?.isNumeric === true;

    const SortingIcon =
        sorted === "asc"
            ? isNumeric ? ArrowUp01 : ArrowUpAZ
            : sorted === "desc"
                ? isNumeric ? ArrowDown01 : ArrowDownAZ
                : null;

    const pointerRef = useRef<{ startX: number; startY: number; button: number } | null>(null);
    const CLICK_MOVE_THRESHOLD = 6;

    const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();

        pointerRef.current = {startX: e.clientX, startY: e.clientY, button: e.button};
    };

    const handleMouseUp = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();

        const started = pointerRef.current;
        pointerRef.current = null;

        if (!isFilterable) return;

        if (!started) return;

        const dx = Math.abs(e.clientX - started.startX);
        const dy = Math.abs(e.clientY - started.startY);

        const moved = dx > CLICK_MOVE_THRESHOLD || dy > CLICK_MOVE_THRESHOLD;

        if (moved) return;

        if (started.button === 0) {
            if (sorted === "asc") {
                column.toggleSorting(true);
            } else if (sorted === "desc") {
                column.clearSorting();
            } else {
                column.toggleSorting(false);
            }

            onHeaderClick?.(columnId, e);
        }
    };

    const handleContextMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isFilterable) return;
        onHeaderClick?.(columnId, e);
    };

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <Button
                    variant="ghost"
                    type="button"
                    onMouseDown={handleMouseDown}
                    onMouseUp={handleMouseUp}
                    onContextMenu={handleContextMenu}
                    className={`absolute inset-0 h-full w-full justify-start gap-0 px-2 text-gray-700 font-medium rounded-none shadow-none ${
                        isFilterable ? "hover:text-gray-900" : "opacity-50 cursor-default"
                    }`}
                    disabled={!isFilterable}
                >
                    <span className="truncate">{name}</span>
                    {SortingIcon && <SortingIcon size={16} className="ml-auto flex-shrink-0"/>}
                </Button>
            </TooltipTrigger>

            <TooltipContent side={"top"}>
                <span>{`Filtruj / Sortuj "${name}"`}</span>
            </TooltipContent>
        </Tooltip>
    );
}

export default AutoTableHeaderFilter;
